import { useMemo, VFC } from 'react';

import { WORKFLOW_OBJECT_FIELD_KEY_MAPPINGS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateWorkflowEditor/constants';
import Accordion from 'dashboard/components/Accordion';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  filterUsableObjects,
  getTokensToDisplayValue,
  snakify,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import { ProductFeatureName } from 'dashboard/models/organization';
import {
  REPORT_TEMPLATE_TYPE_TO_FIELDS,
  REPORT_TEMPLATE_TYPE_TO_WORKFLOW_ACTION,
} from 'dashboard/models/report-template';
import { BaseTrigger, BaseTriggerParams, Trigger } from 'dashboard/models/trigger';
import {
  WorkflowActionMeta,
  WorkflowError,
  WorkflowFieldKey,
  WorkflowFieldObjectKey,
} from 'dashboard/models/workflow';
import {
  WorkflowActionRunReportGenericV1,
  WorkflowStepActionKey,
} from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import useProductFeature from 'lib/hooks/useProductFeature';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

interface ParamsRunReportGenericV1Props extends ParamsBaseProps {
  reportTemplates: WorkflowActionMeta['report-templates'];
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionRunReportGenericV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (
    newParams: WorkflowActionRunReportGenericV1['params'],
    template?: TemplateFilter,
  ) => void;
}

const ParamsRunReportGenericV1: VFC<ParamsRunReportGenericV1Props> = ({
  reportTemplates,
  trigger,
  usableObjects,
  step,
  editable,
  errors,
  params,
  updateActionParams,
  updateStep,
}) => {
  const hasReportTemplatePerm = usePermission(Permission.ReportTemplateView);
  const [showEditPanel, { toggle: toggleEditPanel }] = useToggle(false);
  const hasWatchlistPortraitsEnabled = useProductFeature(
    ProductFeatureName.ReportsWatchlistPortraits,
  );
  const enableBiometrics = useFeatureFlag('report:enable-biometric-watchlists');
  const hasPepPortraitsEnabled = useProductFeature(ProductFeatureName.ReportsPepPortraits);

  const filteredReportTemplates =
    // when report-template-type is non-null, we filter for the specific
    // report template type. otherwise we return all persona-native report templates
    params['report-template-type'] != null
      ? reportTemplates.filter(
          (reportTemplate) => params['report-template-type'] === reportTemplate.type,
        )
      : reportTemplates.filter((reportTemplate) => !reportTemplate.isArchived);

  const reportTemplateOptions = filteredReportTemplates
    .map(({ name, token, type }) => ({
      label: name,
      value: token,
      type,
      fields: REPORT_TEMPLATE_TYPE_TO_FIELDS[type].filter(
        (field) =>
          hasWatchlistPortraitsEnabled ||
          hasPepPortraitsEnabled ||
          enableBiometrics ||
          (field !== 'profile-image' && field !== 'comparison-photo'),
      ),
      reportStepActionKey: REPORT_TEMPLATE_TYPE_TO_WORKFLOW_ACTION[type],
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const options = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [
          WorkflowFieldObjectKey.Account,
          WorkflowFieldObjectKey.Case,
          WorkflowFieldObjectKey.Inquiry,
          WorkflowFieldObjectKey.Transaction,
        ],
        excludeNonPersonaObjects: true,
      },
    }).map(({ value, label }) => ({
      value,
      label,
    }));
  }, [usableObjects]);

  const selectedReportTemplate = reportTemplateOptions.find(
    (option) => option.value === params['report-template-token'],
  );
  const isTemplateAvailable = useMemo(() => {
    return selectedReportTemplate || !params['report-template-token'];
  }, [params, selectedReportTemplate]);
  const getDefaultMappings = (fields: string[]) => {
    return (fields || []).reduce<Record<string, string>>((acc, field) => {
      const snakified = snakify(field);

      if (trigger) {
        switch (trigger.type) {
          case ApiModel.TriggerEvent:
          case ApiModel.TriggerSchedule:
          case ApiModel.TriggerRecurring:
            acc[field] = [BaseTrigger, WorkflowFieldKey.Fields, snakified].join('.');
            break;
          case ApiModel.TriggerApi:
            acc[field] = [BaseTriggerParams, WorkflowFieldKey.Fields, snakified].join('.');
            break;
          case ApiModel.TriggerCase:
          case ApiModel.TriggerAccount:
            acc[field] = [BaseTriggerParams, snakified].join('.');
            break;
          case ApiModel.TriggerDirect: {
            const fieldKey = WORKFLOW_OBJECT_FIELD_KEY_MAPPINGS[field] ?? snakify(field);
            acc[field] = [BaseTrigger, WorkflowFieldKey.Fields, fieldKey].join('.');
            break;
          }
        }
      } else {
        acc[field] = '';
      }

      return acc;
    }, {});
  };

  const handleReportTemplateChange = (value: string) => {
    const reportTemplate = reportTemplateOptions.find((option) => option.value === value);
    if (!reportTemplate) return;

    const defaults = getDefaultMappings(reportTemplate.fields);
    updateStep({
      ...step,
      name: reportTemplate?.label ? `Run ${reportTemplate.label}` : ' Report',
      action: {
        ...(step.action as WorkflowActionRunReportGenericV1),
        params: {
          ...params,
          'report-template-token': reportTemplate?.value,
          'report-fields': defaults,
          'report-step-action-key': reportTemplate?.reportStepActionKey,
        },
      } as WorkflowActionRunReportGenericV1,
    });
  };

  const handleObjectChange = (value: WorkflowActionRunReportGenericV1['params']['object']) => {
    updateActionParams({
      ...params,
      object: value,
      'report-fields': { ...params['report-fields'] },
    });
  };

  const handleEditFieldMappings = (
    value: WorkflowActionRunReportGenericV1['params']['report-fields'],
  ) => {
    updateActionParams({
      ...params,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template-token': selectedReportTemplate?.value,
      'report-fields': value || {},
    });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionRunReportGenericV1['params']>(errors);

  const allowSelfieInput =
    ((enableBiometrics || hasWatchlistPortraitsEnabled) &&
      selectedReportTemplate?.reportStepActionKey === WorkflowStepActionKey.RunReportWatchlist) ||
    (hasPepPortraitsEnabled &&
      selectedReportTemplate?.reportStepActionKey ===
        WorkflowStepActionKey.RunReportPoliticallyExposedPerson);

  const disallowedFieldPaths = allowSelfieInput
    ? []
    : [snakify(`trigger.${WorkflowFieldKey.SelfiePhoto}`)];

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Report Template'
        htmlFor='params-run-report__report-template-input'
        helpText={
          hasReportTemplatePerm
            ? isTemplateAvailable
              ? undefined
              : 'The current report template is not available to you at this time.'
            : 'Your role does not have permissions to view report templates.'
        }
      />
      <ReactSelect
        value={selectedReportTemplate?.value}
        options={reportTemplateOptions}
        onChange={handleReportTemplateChange}
        disabled={!editable || !hasReportTemplatePerm}
        id='params-run-report__report-template'
        hasError={hasErrorFor('report-template-token')}
        placeholder={
          hasReportTemplatePerm ? (isTemplateAvailable ? undefined : 'UNAVAILABLE') : 'RESTRICTED'
        }
      />
      {renderErrorMessageFor('report-template-token')}
      {showEditPanel && (
        <FieldMappingsPanel
          title='Report fields'
          usableObjects={usableObjects}
          fieldMappings={params['report-fields'] ?? {}}
          disallowedFieldPaths={disallowedFieldPaths}
          onOutsideClick={toggleEditPanel}
          onUpdateFieldMappings={handleEditFieldMappings}
        />
      )}
      <Spacer height={2} />
      {isTemplateAvailable && (
        <Accordion defaultExpanded title='Report Fields'>
          {renderErrorMessageFor('report-fields')}
          <Spacer height={1} />
          <FieldsList
            editable={editable && selectedReportTemplate != null}
            fields={params['report-fields']}
            fieldKey='report-fields'
            errors={errors}
            toggleEditPanel={toggleEditPanel}
            tokensToDisplayValue={getTokensToDisplayValue(usableObjects)}
            placeholderEmptyText='Please select a report template'
          />
        </Accordion>
      )}
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <Spacer height={2} />
        <ParamLabel labelText='Object' htmlFor='params-run-report-generic__object-input' />
        <ReactSelect
          value={params.object}
          options={options}
          onChange={handleObjectChange}
          disabled={!editable}
          hasError={hasErrorFor('object')}
          id='params-run-report-generic__object'
        />
        {renderErrorMessageFor('object')}
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsRunReportGenericV1;
